/**
 * swimLoader — the Club tab's selected-day loader (build 16, parity).
 *
 * Scott 19:4x: "what one person's swim looks like on their phone looks like their swim on the other
 * person's phone." Before this a phone held a firehose for each clubmate's LATEST swim only, so every
 * older swim drew native's C5 set summary, which names and splits sets differently from the Sets tab
 * (B-040). Now every swim on the selected day is fetched by UUID (`getClubSwimFirehose`) and derived
 * on the web exactly like the Sets tab (swimCard.setsForSwim).
 *
 * RULES
 * - At most LOADER_CONCURRENCY fetches at once; the rest wait in order.
 * - A swim is wanted at a REVISION: its C5 `labelsRevision` (null = no corrections). An answer at the
 *   wanted revision is never fetched again, so an unchanged snapshot does nothing; a changed revision
 *   refetches that swim only, and the previous answer stays on screen until the new one lands.
 * - Stale-response rejection: an answer is kept only while its swim still wants the revision it was
 *   requested at. An answer whose own `labelsRevision` differs from the one asked for is refetched
 *   once (native moved between C5 and the fetch), then kept.
 * - `notStored` is kept (the row falls back to C5, labelled); an `error` is kept but retried on the
 *   next `want` (the next snapshot or day change), and never replaces an ok answer already on screen.
 *   Nothing here invents sets.
 */
import type { ClubSwimFirehoseResponse, LapLabel, SwimFirehoseResponse } from "@contract/types";
import type { ClubSnapshot } from "./snapshot";
import { localDayKey } from "./strip";

export const LOADER_CONCURRENCY = 3;

export type ShareState = "saved" | "queued" | "rejected";

export type LoadedSwim =
  | { status: "ok"; firehose: SwimFirehoseResponse; labels: LapLabel[]; labelsRevision: string | null; shareState: ShareState | null }
  | { status: "notStored" }
  | { status: "error" };

/** One swim the screen needs. `memberId` is the C5 member id (`snapshot.self` for the viewer). */
export interface SwimRequest {
  memberId: string;
  swimId: string;
  revision: string | null;
}

export type FetchClubSwim = (args: { memberId: string; workoutUUID: string }) => Promise<ClubSwimFirehoseResponse>;

export interface SwimLoader {
  want(requests: readonly SwimRequest[]): void;
  get(swimId: string): LoadedSwim | undefined;
  dispose(): void;
}

/** Every swim on `dayKey`, every member, at the revision C5 carries for it now. */
export function daySwimRequests(snapshot: ClubSnapshot, dayKey: string): SwimRequest[] {
  const out: SwimRequest[] = [];
  for (const member of snapshot.members) {
    for (const swim of member.swims) {
      if (localDayKey(Date.parse(swim.date)) !== dayKey) continue;
      out.push({ memberId: member.id, swimId: swim.id, revision: swim.labelsRevision ?? null });
    }
  }
  return out;
}

function toLoaded(res: ClubSwimFirehoseResponse): LoadedSwim {
  if (res.ok && res.firehose) {
    return { status: "ok", firehose: res.firehose, labels: res.labels ?? [], labelsRevision: res.labelsRevision ?? null, shareState: res.shareState ?? null };
  }
  if (!res.ok && res.reason === "notStored") return { status: "notStored" };
  return { status: "error" };
}

export function createSwimLoader(fetch: FetchClubSwim, onChange: () => void, concurrency = LOADER_CONCURRENCY): SwimLoader {
  const wanted = new Map<string, SwimRequest>();
  const cache = new Map<string, { revision: string | null; result: LoadedSwim }>();
  /** `swimId|revision` of every request queued or in flight. */
  const pending = new Set<string>();
  const keyOf = (req: SwimRequest) => `${req.swimId}|${req.revision}`;
  const queue: { req: SwimRequest; retried: boolean }[] = [];
  let active = 0;
  let disposed = false;

  function pump(): void {
    while (!disposed && active < concurrency && queue.length > 0) {
      const job = queue.shift()!;
      // Superseded while it waited: a newer revision is wanted now.
      if (wanted.get(job.req.swimId)?.revision !== job.req.revision) {
        pending.delete(keyOf(job.req));
        continue;
      }
      start(job.req, job.retried);
    }
  }

  function start(req: SwimRequest, retried: boolean): void {
    active++;
    fetch({ memberId: req.memberId, workoutUUID: req.swimId })
      .then(toLoaded, () => ({ status: "error" }) as LoadedSwim)
      .then((result) => {
        active--;
        if (disposed) return;
        const stillWanted = wanted.get(req.swimId)?.revision === req.revision;
        if (stillWanted) {
          if (result.status === "ok" && result.labelsRevision !== req.revision && !retried) {
            queue.unshift({ req, retried: true });
            pump();
            return;
          }
          if (result.status === "error" && cache.get(req.swimId)?.result.status === "ok") {
            // A failed refetch keeps the good answer on screen; the next want retries (its revision
            // still differs from the cached one).
          } else {
            cache.set(req.swimId, { revision: req.revision, result });
            onChange();
          }
        }
        pending.delete(keyOf(req));
        pump();
      });
  }

  return {
    want(requests) {
      if (disposed) return;
      for (const req of requests) {
        wanted.set(req.swimId, req);
        const have = cache.get(req.swimId);
        if (have && have.revision === req.revision && have.result.status !== "error") continue;
        if (pending.has(keyOf(req))) continue;
        pending.add(keyOf(req));
        queue.push({ req, retried: false });
      }
      pump();
    },
    get(swimId) {
      return cache.get(swimId)?.result;
    },
    dispose() {
      disposed = true;
      queue.length = 0;
    },
  };
}
